import { Component, computed, inject, ChangeDetectionStrategy } from "@angular/core";

import { IButtonDirective, IIconComponent, ModalOptions, ModalService } from "@front/m19-ui";
import { TacticComponent } from "@m19-board/strategies/tactic/tactic.component";
import { CampaignType, TacticType } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { SegmentConfigType, SegmentEx } from "@front/m19-models";
import {
  TacticAddPopupComponent,
  TacticAddPopupModalData,
} from "@m19-board/strategies/strategy-page/tactic-add-popup.component";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountSelectionService, SegmentService } from "@front/m19-services";
import { switchMap } from "rxjs/operators";
import { startWith } from "rxjs";
import { TargetingLimit } from "@m19-board/strategies/target-utils";
import { TargetLimitWarningComponent } from "@m19-board/strategies/target-limit-warning/target-limit-warning.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";

@Component({
  selector: "app-strategy-page-tactics",
  imports: [IButtonDirective, IIconComponent, TacticComponent, TranslocoDirective, TargetLimitWarningComponent],
  templateUrl: "./strategy-page-tactics.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageTacticsComponent {
  private readonly strategyPageService =
    inject<StrategyPageService<CampaignType.SP | CampaignType.SB>>(StrategyPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly segmentService = inject(SegmentService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  protected readonly CampaignType = CampaignType;

  strategy = this.strategyPageService.strategy;
  isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  segmentIndex = toSignal<Map<number, SegmentEx>>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      startWith(new Map()),
    ),
    { requireSync: true },
  );
  targetLimit = computed<TargetingLimit | undefined>(() => {
    const strategy = this.strategy();
    if (this.segmentIndex().size === 0 || !strategy) return undefined;

    const segments = strategy.tactics
      .filter((t) => t.tacticType === TacticType.LEGACY)
      .map((t) => this.segmentIndex().get(t.segmentId)!);
    const blacklist = strategy.tactics
      .filter((t) => t.tacticType === TacticType.BLACKLIST)
      .map((t) => this.segmentIndex().get(t.segmentId)!);
    return new TargetingLimit(segments, blacklist);
  });
  productSegmentDisabled = computed(() => {
    const strategy = this.strategy();
    if (!strategy) return true;

    return !strategy.isSpStrategy() || !strategy.productTargetingEnabled;
  });

  createTactic(): void {
    const strategy = this.strategy()!;
    const allowedSegmentTacticTypes: [SegmentConfigType, TacticType][] = [];
    if (strategy.campaignType != CampaignType.SD) {
      allowedSegmentTacticTypes.push([SegmentConfigType.KeywordSegment, TacticType.LEGACY]);
      allowedSegmentTacticTypes.push([SegmentConfigType.KeywordSegment, TacticType.BLACKLIST]);
    }
    if (strategy.isSpStrategy() && strategy.productTargetingEnabled) {
      allowedSegmentTacticTypes.push([SegmentConfigType.ProductSegment, TacticType.LEGACY]);
      allowedSegmentTacticTypes.push([SegmentConfigType.ProductSegment, TacticType.BLACKLIST]);
    }

    const modalOptions: ModalOptions<TacticAddPopupModalData> = {
      data: {
        strategy: this.strategy()!,
        allowedSegmentTacticTypes: allowedSegmentTacticTypes,
      },
      modalTitle: this.translocoService.translate("tactic-add-popup.add_a_new_tactic"),
    };
    this.modalService.openModal(TacticAddPopupComponent, modalOptions);
  }
}
